import type { FontMap, TextTheme } from '../../../../../../../UtilsProvider';
import { useUtils } from '../../../../../../../UtilsProvider';
import type { UseLiveTextStyle, UseLiveTextStyleProps } from './useLiveTextStyle.types';

const normalizeFontSize = (currentFontSize: number, fontSizes: number[]): number => {
  const minX = Math.min(...fontSizes);
  const maxX = Math.max(...fontSizes);
  const minFontSize = 14;
  const maxFontSize = 36;
  const diff = maxX - minX || 1;
  return Math.ceil(minFontSize + ((currentFontSize - minX) * (maxFontSize - minFontSize)) / diff);
};

const isTextTheme = (theme: TextTheme | FontMap): theme is TextTheme => (theme as TextTheme).fontSize !== undefined;

const getFontSize = (theme: TextTheme | FontMap): number =>
  parseInt(isTextTheme(theme) ? theme.fontSize : theme.size, 10);

export const useLiveTextStyle = ({ themeName }: UseLiveTextStyleProps): UseLiveTextStyle => {
  const { shouldUseTextThemes } = useUtils();
  const isTextThemesExperiment = shouldUseTextThemes();

  const {
    theme: { fonts, textThemes },
  } = useUtils();

  if (!themeName) {
    return {};
  }

  const themeMap = isTextThemesExperiment ? textThemes?.getAll() : fonts?.getMap();

  const style = themeMap[themeName];

  if (!style) {
    return {};
  }

  const fontSizes = Object.keys(themeMap)
    .filter((theme) => theme !== 'font_1' && theme !== 'font_10')
    .map((theme) => getFontSize(themeMap[theme]));

  const fontSize = normalizeFontSize(getFontSize(style), fontSizes);

  const computedStyle = {
    fontWeight: isTextTheme(style) ? style.fontWeight : style.weight,
    fontFamily: isTextTheme(style)
      ? style.fontFamily.replace(/[+]/, ' ')
      : `${style.family},${style.fontWithFallbacks}`,
    fontSize,
    fontStyle: isTextTheme(style) ? style.fontStyle : style.style,
  };

  const textThemeExtraProps = isTextTheme(style) && {
    lineHeight: style.lineHeight,
    letterSpacing: style.letterSpacing,
  };

  return isTextThemesExperiment ? { ...computedStyle, ...textThemeExtraProps } : computedStyle;
};
